import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { TEST_IDS } from "../../constants/testIds";
import { useAuth } from "../../lib/auth";
import { Button } from "../ui/button";

const links = [
  { to: "/", label: "Home", tid: TEST_IDS.nav.home },
  { to: "/about", label: "About", tid: TEST_IDS.nav.about },
  { to: "/strategies", label: "Strategies", tid: TEST_IDS.nav.strategies },
  { to: "/initiatives", label: "Strategic Initiatives", tid: "nav-initiatives" },
  { to: "/portfolio", label: "Portfolio", tid: TEST_IDS.nav.portfolio },
  { to: "/insights", label: "Insights", tid: TEST_IDS.nav.insights },
  { to: "/contact", label: "Contact", tid: TEST_IDS.nav.contact },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 transition-all ${scrolled ? "backdrop-blur-md bg-[#F8F7F2]/85 border-b border-[#0B1F3A]/10" : "bg-transparent"}`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 h-20">
        <Link to="/" data-testid={TEST_IDS.nav.logo} className="flex items-center">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.tid}
              className={({ isActive }) =>
                `text-[13px] tracking-wide uppercase gold-underline ${isActive ? "text-[#0B1F3A] font-semibold" : "text-[#5F6B7A]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Link to="/portal" data-testid={TEST_IDS.nav.portal}>
                <Button
                  variant="outline"
                  className="rounded-none border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-[#F8F7F2] text-xs uppercase tracking-widest h-10 px-5"
                >
                  {user.role === "admin" ? "Admin" : "Portal"}
                </Button>
              </Link>
              <button
                onClick={logout}
                data-testid={TEST_IDS.nav.logout}
                className="text-[11px] uppercase tracking-widest text-[#5F6B7A] hover:text-[#0B1F3A]"
              >
                Sign out
              </button>
            </>
          ) : (
            <Link to="/login" data-testid={TEST_IDS.nav.login}>
              <Button
                className="rounded-none bg-[#0B1F3A] hover:bg-[#3399FF] text-[#F8F7F2] text-xs uppercase tracking-widest h-10 px-5"
              >
                Investor Login
              </Button>
            </Link>
          )}
        </div>

        <button
          className="md:hidden text-[#0B1F3A]"
          onClick={() => setOpen(v => !v)}
          data-testid={TEST_IDS.nav.mobileToggle}
          aria-label="menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#0B1F3A]/10 bg-[#F8F7F2]">
          <div className="px-6 py-5 flex flex-col gap-4">
            {links.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.tid}-mobile`}
                className="text-[13px] uppercase tracking-widest text-[#0B1F3A]"
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <Link to="/portal" className="text-[13px] uppercase tracking-widest text-[#0B1F3A]">Portal</Link>
                <button onClick={logout} className="text-left text-[13px] uppercase tracking-widest text-[#5F6B7A]">Sign out</button>
              </>
            ) : (
              <Link to="/login" className="text-[13px] uppercase tracking-widest text-[#D4AF37]">Investor Login</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
